import { Card, Divider, Text, Paper, Skeleton, Switch, ColorSwatch, Stack, ScrollArea, Group, Menu } from '@mantine/core';
import { useDataProvider } from '../../../data_provider/StandardDataProvider';
import { useEffect, useState } from 'react';
import { AutoRefreshToggle, AutoRefreshInfo } from './AutoRefresh';
import { formatDistance, formatDistanceToNow, isValid as isDateValid } from 'date-fns';
import _ from 'lodash'
import { DataLoadingError } from './Common';
import { useOutletContext } from 'react-router-dom';
import { formatInTimeZone } from "date-fns-tz"

const DEFAULT_AUTO_REFRESH_DURATION = 60000*5


const EventsWidget = ({}) => {

    const {data, requestId, isDataReady, isLoading, isError, errorMessage, lastRefreshTs, refreshData, autoRefreshEnabled, enableAutoRefresh, disableAutoRefresh} = useDataProvider({
        requestMethod: "GET",
        requestUrl: "/api/dashboard/lighthouse/data/events",
        resetDataOnRequest: false,
        autoRefreshDuration: DEFAULT_AUTO_REFRESH_DURATION
    })
    const [showContextData, setShowContextData] = useState(false)

    useEffect(() => {
        //refreshData()
        enableAutoRefresh()
    }, [])

    return (
        <Card withBorder p={0} style={{flexGrow: 1, maxWidth: "4450px", minWidth: "0px", height: "100%"}}>
            <Group px={10} pt={10} pb={5} position="apart" align="baseline" style={{flexShrink: 0}}>
                <Text weight={500}>Recent Events</Text>
                <Group spacing={4}>
                    <AutoRefreshInfo
                        lastRefreshTs={lastRefreshTs}
                        isLoading={isLoading}
                        isError={isError}
                        autoRefreshEnabled={autoRefreshEnabled}
                    />
                    <Menu>
                        <Paper p="sm">
                            <AutoRefreshToggle
                                autoRefreshEnabled={autoRefreshEnabled}
                                enableAutoRefresh={enableAutoRefresh}
                                disableAutoRefresh={disableAutoRefresh}
                            />
                        </Paper>
                        <Paper p="sm">
                            <DisplayContextDataToggle
                                showContextData={showContextData}
                                setShowContextData={setShowContextData}
                            />
                        </Paper>
                    </Menu>
                </Group>
            </Group>
            <Divider />
            <Stack p={0} style={{height: "100%"}}>
                <ScrollArea p="md">
                    <Stack spacing="xs" p={0} style={{paddingBottom: "45px"}}>
                        {isError && <DataLoadingError message={errorMessage} />}
                        {data && <EventList data={data} showContextData={showContextData} />}
                        {!data && !isError && isLoading && <DataLoadingSkeleton />}
                    </Stack>
                </ScrollArea>
            </Stack>
        </Card>
    )
}

const DisplayContextDataToggle = ({showContextData, setShowContextData}) => {
    return (
        <span className="flow-horizontal" style={{alignItems: "center"}}>
            <Switch
                checked={showContextData}
                onChange={(event) => setShowContextData(event.currentTarget.checked)}
                style={{marginRight: "10px"}}
                size="xs"
            />
            <Text size="xs" color="dimmed">Show Context Data</Text>
        </span>
    )
}

const EventList = ({data, showContextData}) => {

    const openEvents = _.sortBy(data.openEvents, "eventOpenedTs").reverse()
    const closedEvents = _.sortBy(data.closedEvents, "eventClosedTs").reverse()

    const openEventComponents = openEvents.map(x => <OpenEventCard event={x} showContextData={showContextData} />)
    const closedEventComponents = closedEvents.map(x => <ClosedEventCard event={x} showContextData={showContextData} />)

    return (
        <>
            <Divider label="Open Events" />
            {openEventComponents}
            <Divider  label="Recently Closed Events" />
            {closedEventComponents}
        </>
    )
}

const DataLoadingSkeleton = () => {
    return (
        <>
            <Skeleton height={75} />
            <Skeleton height={75} />
            <Skeleton height={75} />
        </>
    )
}


const OpenEventCard = ({event, ...props}) => {

    const { assetFrameworkServices } = useOutletContext()
    const site = assetFrameworkServices.getSites(event.assets?.site)
    const timezone = site?.meta?.timezone || "UTC"

    const eventOpenedDate = new Date(event.eventOpenedTs)

    const formattedDuration = isDateValid(eventOpenedDate) ? formatDistanceToNow(eventOpenedDate) : null
    const formattedOpenDate = isDateValid(eventOpenedDate) ? formatInTimeZone(eventOpenedDate, timezone, "h:mm b, E LLL d y") : null

    return (
        <EventCard
            type="open"
            event={event}
            formattedDate={formattedOpenDate}
            formattedDuration={formattedDuration}
            {...props}
        />
    )
}
const ClosedEventCard = ({event, ...props}) => {

    const { assetFrameworkServices } = useOutletContext()
    const site = assetFrameworkServices.getSites(event.assets?.site)
    const timezone = site?.meta?.timezone || "UTC"
    
    const eventOpenedDate = new Date(event.eventOpenedTs)
    const eventClosedDate = new Date(event.eventClosedTs)

    const formattedDuration = isDateValid(eventOpenedDate) && isDateValid(eventClosedDate) ? formatDistance(eventOpenedDate, eventClosedDate) : null
    const formattedCloseDate = isDateValid(eventClosedDate) ? formatInTimeZone(eventClosedDate, timezone, "h:mm b, E LLL d y") : null

    return (
        <EventCard
            type="closed"
            event={event}
            formattedDate={formattedCloseDate}
            formattedDuration={formattedDuration}
            {...props}
        />
    )
}
const EventCard = ({type, event, formattedDate, formattedDuration, showContextData}) => {

    const [isContextVisible, setIsContextVisible] = useState(showContextData)
    const toggleIsContextVisible = () => {
        setIsContextVisible((v) => !v)
    }

    useEffect(() => {
        setIsContextVisible(showContextData)
    }, [showContextData])

    return (
        <Card py={5} px={10} withBorder style={{flexGrow: 1, minWidth: "0px"}}>
                <EventCardHeader
                    type={type}
                    formattedDuration={formattedDuration}
                    formattedDate={formattedDate}
                />
                <EventCardMainContent
                    siteName={event.siteName}
                    eventTemplateName={event.eventTemplateName}
                    eventTemplateDescription={event.eventTemplateDescription}
                    eventTemplateColor={event.eventTemplateColor}
                />
                <Divider className="event-toggle-context-data" my={4} label={isContextVisible ? "Hide Context" : "Show Context"} labelPosition="center" onClick={toggleIsContextVisible} />
                {isContextVisible &&
                    <EventContextData event={event} />
                }
        </Card>
    )
}
const EventCardHeader = ({type, formattedDate, formattedDuration}) => {
    return (
        <Group spacing={0} style={{justifyContent: "space-between"}}>
            <span className="flow-horizontal align-bl" style={{flexShrink: 0}}>
                {type==="open" && <ColorSwatch size={10} color="red" />}
                {type==="closed" && <ColorSwatch size={10} color="green" />}
                <Text size="xs" color="dimmed" ml={6}>{formattedDuration}</Text>
            </span>
            <div className="flow-vertical" style={{flexShrink: 0}}>
                <Text size="xs" color="dimmed">{formattedDate}</Text>
            </div>
        </Group>
    )
}
const EventCardMainContent = ({siteName, eventTemplateName, eventTemplateDescription, eventTemplateColor}) => {
    return (
        <div className="flow-vertical">
            <div className="flow-horizontal align-bl">
                <Text size="md" weight={500}>{siteName}</Text>
                <EventCodeBadge color={eventTemplateColor}>{eventTemplateName}</EventCodeBadge>
            </div>
            <Text size="xs" color="dimmed">{eventTemplateDescription}</Text>
        </div>
    )
}
const EventContextData = ({event}) => {

    let contextComponent

    const context = event.eventDetail
    try {
        const contextSerialized = JSON.parse(context)
        let contextParseField
        if (contextSerialized.user_context?.data) contextParseField = contextSerialized.user_context.data
        if (contextSerialized.user_context?.outputs) contextParseField = contextSerialized.user_context.outputs

        if (contextParseField) {
            contextComponent = contextParseField.map(data => {
                return (
                    <MetricLineItem
                        name={data.name}
                        value={data.value}
                        units={data.units}
                    />
                )
            })
        }
        else {
            contextComponent = <div>{context}</div>
        }
    } catch {
        contextComponent = <div>{context}</div>
    }

    return contextComponent
}

const MetricLineItem = ({name, value, units}) => {

    const formattedValue = typeof(value) === "number" ? value.toLocaleString(undefined, {maximumFractionDigits: 1}) : null

    return (
        <div style={{display: "flex", flexDirection: "row", alignItems: "baseline", paddingRight: "0px"}}>
            <Text size="xs" color="dimmed" mr={6}>{name}: </Text>
            <Text size="sm">{formattedValue}</Text>
            {units && <Text size="xs" color="dimmed" component="i" style={{marginLeft: "4px"}}>{units}</Text>}
        </div>
    )
}

const EventCodeBadge = ({color, ...props}) => {
    return (
        <div style={{
            backgroundColor: color || "gray",
            padding: "0 5px",
            display: "inline-flex",
            alignItems: "center",
            borderRadius: "32px",
            fontWeight: "700px",
            marginLeft: "6px",
            letterSpacing: "0.25px",
            justifyContent: "center",
            height: "16px",
            fontSize: "9px"
        }}>
            <span style={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "white"}}>{props.children}</span>
        </div>
    )
}

export default EventsWidget